import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { ShiftType } from "../../../utils/models";
import { shiftTypeToIndex } from "../../../utils/utilFunctions";
import { formatShiftDate, formatShiftHours } from "../../AddNurse/addStaffUtils";
import { roundedWindowKey } from "../../staffing/MonthlyView/jobView/monthlyJobUtils";
import { REMOVE_STAFF_OTHER_WINDOW_KEY, type RemoveStaffWindow } from "./removeStaffWindows";

/** Analytics `source` of the Remove Staff panel's own events (date change, multi-remove lifecycle). */
export const REMOVE_STAFF_ANALYTICS_SOURCE = "remove_staff_panel";

/** The "All shifts" pill — no window filter. */
export const REMOVE_STAFF_ALL_FILTER = "all";

/** One pick = the single remove's copy; more = the multi-remove's counts. */
export const SINGLE_REMOVE_COUNT = 1;

/** Day (0) / night (1) slot a window's sun or moon is drawn for — the same slot index `ShiftWindowIcon` reads. */
export const windowShiftIndex = (window: Pick<RemoveStaffWindow, "isNight">): number => shiftTypeToIndex(window.isNight ? ShiftType.Night : ShiftType.Day);

/**
 * Where a caller wants the list to open: the issue's own slot, and its hours when it knows them. A caller that
 * opens on a whole day (the Staffing Changes panel) passes none and the list opens on "All shifts".
 */
export type RemoveStaffWindowHint = { shiftIndex: number; hours: { start: DateTime; end: DateTime } | null };

type FilterArgs = {
	/** The pill the user picked; `null` until they pick one. */
	explicit: string | null;
	hint: RemoveStaffWindowHint | null;
	windows: readonly RemoveStaffWindow[];
	otherCount: number;
};

const hasShifts = (key: string, windows: readonly RemoveStaffWindow[], otherCount: number): boolean => {
	if (key === REMOVE_STAFF_ALL_FILTER) return true;
	if (key === REMOVE_STAFF_OTHER_WINDOW_KEY) return otherCount > 0;
	return windows.some((window) => window.key === key && window.shiftCount > 0);
};

/** The window a hint points at: its hours' own window, else the first staffed-and-worked window of its slot. */
const windowKeyForHint = (hint: RemoveStaffWindowHint, windows: readonly RemoveStaffWindow[], otherCount: number): string | null => {
	if (hint.hours) {
		const key = roundedWindowKey(hint.hours.start, hint.hours.end);
		if (hasShifts(key, windows, otherCount)) return key;
	}
	const sameSlot = windows.find((window) => window.shiftCount > 0 && windowShiftIndex(window) === hint.shiftIndex);
	return sameSlot ? sameSlot.key : null;
};

/**
 * The pill that is on. The user's pick wins; before one, the caller's hint; and any pick whose window has no
 * shifts on this date (a date change, or everyone in it removed) falls back to "All shifts" rather than
 * leaving an empty list behind a pill that is disabled.
 */
export const resolveWindowFilter = ({ explicit, hint, windows, otherCount }: FilterArgs): string => {
	const candidate = explicit ?? (hint ? windowKeyForHint(hint, windows, otherCount) : null);
	return candidate !== null && hasShifts(candidate, windows, otherCount) ? candidate : REMOVE_STAFF_ALL_FILTER;
};

/** "7:00 AM–3:00 PM" — a window's hours, spelled like every shift window in Add / Remove Staff. */
export const formatWindowHours = (window: Pick<RemoveStaffWindow, "start" | "end">): string => formatShiftHours(window);

/** What the footer reads back before the button acts: "Brittany Frantz · Tue, Sep 22 ☀ 8:00 AM–3:00 PM" or "3 staff · Tue, Sep 22". */
export type RemoveStaffSummary = {
	who: string;
	date: string;
	/** Only for one pick — several picks can sit in different windows. */
	hours: string | null;
	/** Day (0) / night (1) slot of that one pick's hours, for its sun / moon. */
	shiftIndex: number | null;
};

/** One picked shift as the footer and the snackbars name it. */
export type RemoveStaffPick = { name: string; start: DateTime; end: DateTime; shiftIndex: number };

export const buildRemoveStaffSummary = (picks: readonly RemoveStaffPick[], staffingDate: DateTime, t: TFunction): RemoveStaffSummary => {
	const date = formatShiftDate(staffingDate);
	if (picks.length === 0) return { who: t("remove.staff.summary.pick"), date, hours: null, shiftIndex: null };
	if (picks.length > SINGLE_REMOVE_COUNT) return { who: t("remove.staff.summary.count", { count: picks.length }), date, hours: null, shiftIndex: null };
	const [pick] = picks;
	return { who: pick.name, date, hours: formatShiftHours(pick), shiftIndex: pick.shiftIndex };
};

/** "Remove from shift" for one (or none yet), "Remove 3 from shift" for several. */
export const removeButtonLabel = (selectedCount: number, t: TFunction): string =>
	selectedCount > SINGLE_REMOVE_COUNT ? t("remove.staff.confirm.count", { count: selectedCount }) : t("remove.staff.confirm");

/** "Ana Lee (7:00 AM–3:00 PM)" — a pick named with its hours, so two shifts of one nurse read apart. */
export const pickLabel = (name: string, hours: { start: DateTime; end: DateTime }, t: TFunction): string =>
	t("remove.staff.pick.label", { name, hours: formatShiftHours(hours) });

/** Names joined for one sentence: "Ana, Ben and Cy". The conjunction is translated; a name is never split. */
export const joinNames = (names: readonly string[], t: TFunction): string => {
	if (names.length <= 1) return names.join("");
	return t("remove.staff.names.list", { names: names.slice(0, -1).join(", "), last: names[names.length - 1] });
};
